import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle } from "@ionic/react";
import { httpsCallable } from "firebase/functions";
import { useCallback, useEffect, useRef, useState } from "react";
import { functions } from "../../App";

type BackfillResult = {
  totalQuestions: number;
  missingIrt: number;
  wouldUpdateCount: number;
  updatedCount: number;
  skippedCount: number;
  dryRun?: boolean;
};

const BackfillInitializeIrtQuestions: React.FC = () => {
  const [running, setRunning] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [preview, setPreview] = useState<BackfillResult | null>(null);
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewLoading, setPreviewLoading] = useState(true);
  const isMountedRef = useRef(true);

  const callBackfill = async (dryRun: boolean) => {
    const callable = httpsCallable(functions, "adminBackfillInitializeIrtQuestions");
    const response = await callable({ dryRun });
    return response.data as BackfillResult;
  };

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewMessage("");
    try {
      const payload = await callBackfill(true);
      if (!isMountedRef.current) {
        return;
      }
      setPreview(payload);
      setPreviewMessage("");
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      setPreviewMessage(`Preview failed: ${String(error)}`);
    } finally {
      if (isMountedRef.current) {
        setPreviewLoading(false);
      }
    }
  }, []);

  const runBackfill = async () => {
    if (running) {
      return;
    }
    setRunning(true);
    setResultMessage("");
    setResult(null);
    try {
      const payload = await callBackfill(false);
      setResult(payload);
      setResultMessage("IRT backfill completed successfully.");
      await loadPreview();
    } catch (error) {
      setResultMessage(`Backfill failed: ${String(error)}`);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    loadPreview().catch(() => undefined);
    return () => {
      isMountedRef.current = false;
    };
  }, [loadPreview]);

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>IRT Question Initialization</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>
          Initializes missing IRT parameters on all questions and fills in missing uncertainty fields.
        </p>
        {previewLoading && <p>Loading preview...</p>}
        {preview && (
          <>
            <p><b>Total questions:</b> {preview.totalQuestions}</p>
            <p><b>Missing IRT params:</b> {preview.missingIrt}</p>
            <p><b>Questions to update:</b> {preview.wouldUpdateCount}</p>
          </>
        )}
        {previewMessage && <p>{previewMessage}</p>}
        {result && (
          <>
            <p><b>Updated questions:</b> {result.updatedCount}</p>
            <p><b>Skipped questions:</b> {result.skippedCount}</p>
          </>
        )}
        <IonButton
          expand="block"
          onClick={runBackfill}
          disabled={running}
          aria-label="Run IRT question initialization backfill"
          title="Run IRT question initialization backfill"
        >
          {running ? "Running..." : "Run Backfill"}
        </IonButton>
        {resultMessage && <p>{resultMessage}</p>}
      </IonCardContent>
    </IonCard>
  );
};

export default BackfillInitializeIrtQuestions;
